import React, { FC, useMemo } from "react";
import { Cascader, Form, FormInstance, Input } from "antd";
import { MatterTemplateData, PracticeArea } from "../../data-types";
import { OrgUnitCascaderValue } from "../../utils";
import { VPDLabel } from "~/context/taxonomy-context";

export type MatterTemplateFormValues = {
  practiceAreaId: OrgUnitCascaderValue;
  title: string;
};

export type MatterTemplateFormProps = {
  form: FormInstance<MatterTemplateFormValues>;
  practiceAreas: PracticeArea[];
  initialValue?: { title: string };
  onSave: (data: MatterTemplateData) => void;
};

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
}

export const MatterTemplateForm: FC<MatterTemplateFormProps> = ({
  form,
  practiceAreas,
  initialValue,
  onSave,
}) => {
  const initialFormValues = useMemo(
    () => (initialValue ? dataToFormValues(initialValue) : undefined),
    [initialValue, practiceAreas]
  );

  const PA_LABEL = VPDLabel("PracticeArea");

  const paOptions: Option[] = practiceAreas
    .filter((x) => !x.parent?.id)
    .map((pa) => {
      return {
        value: pa.id,
        label: pa.name,
        children:
          practiceAreas
            .filter((x) => x.parent?.id === pa.id)
            .map((y) => ({ value: y.id, label: y.name })) || [],
      };
    });

  return (
    <Form
      form={form}
      onFinish={(values) => onSave(formValuesToData(values))}
      labelCol={{ style: { width: "10em" } }}
      wrapperCol={{ flex: 1 }}
      initialValues={initialFormValues}
    >
      <Form.Item
        name="practiceAreaId"
        label={`${PA_LABEL}`}
        rules={[
          {
            required: true,
            message: `Please select a ${PA_LABEL}`,
          },
        ]}
      >
        <Cascader
          showSearch={true}
          allowClear
          placeholder={`Select ${PA_LABEL}`}
          options={paOptions}
        />
      </Form.Item>
      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: "Please enter a title" }]}
      >
        <Input placeholder="Enter matter title" autoComplete="off" />
      </Form.Item>
    </Form>
  );
};

export const dataToFormValues = (data: { title: string }): { title: string } => {
  const { title } = data;
  return {
    title,
  };
};

const formValuesToData = ({
  practiceAreaId,
  title,
}: MatterTemplateFormValues): MatterTemplateData => ({
  practiceAreaId: orgUnitFromCascadeValue(practiceAreaId),
  title,
});

export const orgUnitFromCascadeValue = (value: OrgUnitCascaderValue): number => {
  if (typeof value === "number") {
    return value;
  }

  if (!value) {
    return 0;
  }

  if (value?.[0] && value?.[1]) {
    return value[1];
  }

  if (value?.[0]) {
    return value[0];
  }

  return 0;
};
